import { useEffect, useState } from "react";
import { BookOpen, Compass, Flame, Mail } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const FACE = {
  explorer: { icon: Compass, tint: "border-brand/40 bg-brand/10 text-brand" },
  marathon: { icon: Flame, tint: "border-[#F5C451]/40 bg-[#F5C451]/10 text-[#F5C451]" },
  letter: { icon: Mail, tint: "border-forest/40 bg-forest/10 text-forest" },
  fifty: { icon: BookOpen, tint: "border-white/25 bg-white/10 text-ink" },
};

const NOTE = {
  explorer: "Been round all four page walkthroughs",
  marathon: "Chatted thirty days in a row",
  letter: "Wrote and kept a translated letter",
  fifty: "Opened or listened to fifty news stories",
};

const NAME = { explorer: "Explorer", marathon: "Marathon", letter: "First Letter",
               fifty: "Fifty Read" };

let asked = null;   // one look per page load, however many places show them

// One earned badge, as small as it can be
export const Badge = ({ badgeKey, withWords = false }) => {
  const face = FACE[badgeKey];
  if (!face) return null;
  const Icon = face.icon;
  return (
    <span
      data-testid={`badge-${badgeKey}`}
      title={NOTE[badgeKey]}
      className={`inline-flex shrink-0 items-center gap-1 rounded-full border px-1.5 py-0.5 text-[10px] font-semibold ${face.tint}`}
    >
      <Icon className="h-3 w-3" />
      {withWords ? NAME[badgeKey] : ""}
    </span>
  );
};

// The badges the member has earned themselves
export const useMyBadges = () => {
  const { user } = useAuth();
  const [badges, setBadges] = useState([]);

  useEffect(() => {
    if (!user) return;
    if (!asked) asked = api.get("/badges/mine").then(({ data }) => data.badges || []);
    asked.then(setBadges).catch(() => {});
  }, [user]);

  return badges;
};

// Whatever these other members have earned, for the names beside messages
export const useCrewBadges = (ids) => {
  const [crew, setCrew] = useState({});
  const key = [...new Set(ids.filter(Boolean))].sort().join(",");

  useEffect(() => {
    if (!key) return;
    api.post("/badges/who", { user_ids: key.split(",") })
      .then(({ data }) => setCrew(data.people || {}))
      .catch(() => {});
  }, [key]);

  return crew;
};

export const CrewBadges = ({ keys }) => (
  <>{(keys || []).map((k) => <Badge key={k} badgeKey={k} />)}</>
);

// Everything the member has earned, worn small
export const MyBadges = ({ withWords = false, max = 4 }) => {
  const badges = useMyBadges();
  const won = badges.filter((b) => b.earned).slice(0, max);
  if (!won.length) return null;
  return (
    <span className="inline-flex shrink-0 items-center gap-1" data-testid="my-badges">
      {won.map((b) => <Badge key={b.key} badgeKey={b.key} withWords={withWords} />)}
    </span>
  );
};

// The full shelf, with what is still to come — for the Settings page
export const BadgeShelf = () => {
  const badges = useMyBadges();
  if (!badges.length) return null;
  return (
    <div className="rounded-3xl border border-white/10 bg-panel p-8" data-testid="badge-shelf">
      <h2 className="font-display text-2xl font-bold tracking-tight">Your badges</h2>
      <p className="mt-1 text-sm text-ink-soft">
        Small marks for using ChatMiLu, not for paying for it. They show beside your name.
      </p>
      <ul className="mt-5 space-y-2">
        {badges.map((b) => (
          <li
            key={b.key}
            data-testid={`badge-row-${b.key}`}
            className={`flex flex-wrap items-center gap-3 rounded-2xl border px-4 py-3 text-sm ${
              b.earned ? "border-white/12 bg-page" : "border-white/8 bg-page/40 opacity-70"}`}
          >
            <Badge badgeKey={b.key} />
            <span className="font-semibold">{NAME[b.key]}</span>
            <span className="text-ink-soft">{NOTE[b.key]}</span>
            <span className="ms-auto shrink-0 tabular-nums text-ink-soft">
              {b.earned ? "earned" : `${b.have} of ${b.goal}`}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
};
